import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Store } from 'lucide-react';
import { toast } from 'react-toastify';
import { useLoginUser } from '../../api';
import { login } from '../../redux/slice/authSlice';
import { userRoles } from '../../constants/userRoles';
import BrandLogo from '../../components/brand/BrandLogo';
import styles from './login.module.css';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const loginMutation = useLoginUser();

  if (user?.role === userRoles.vender) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = await loginMutation.mutateAsync({ email, password });
      if (data?.user?.role !== userRoles.vender) {
        toast.error('This account is not a registered vendor partner account.');
        return;
      }
      dispatch(login({ user: data.user, token: data.token }));
      toast.success(`Welcome back, ${data.user.name || 'Partner'}!`);
      const redirectTo = location.state?.from || '/dashboard';
      navigate(redirectTo, { replace: true });
    } catch (error) {
      const message = error?.response?.data?.error || error?.response?.data?.message;
      toast.error(message || 'Login failed. Please check your vendor credentials.');
    }
  };

  return (
    <div className={styles.authPage}>
      {/* Left Atmospheric Partner Showcase */}
      <div className={styles.brandHero}>
        <div className={styles.heroBrandWrapper}>
          <BrandLogo variant="lockup" tone="onDark" />
        </div>

        <div className={styles.heroContent}>
          <div className={styles.heroBadge}>
            <Store size={13} />
            <span>Salon Partner Network</span>
          </div>

          <h1 className={styles.heroTitle}>
            Grow Your <span className={styles.heroHighlight}>Salon</span> Business.
          </h1>

          <p className={styles.heroDescription}>
            Manage booking slots, supervise treatments and pricing, receive automated bank payouts,
            and connect with hundreds of local clients.
          </p>

          <div className={styles.heroStatsGrid}>
            <div className={styles.heroStatItem}>
              <span className={styles.heroStatValue}>500+</span>
              <span className={styles.heroStatLabel}>Partner Salons</span>
            </div>
            <div className={styles.heroStatItem}>
              <span className={styles.heroStatValue}>Daily</span>
              <span className={styles.heroStatLabel}>Bank Payouts</span>
            </div>
            <div className={styles.heroStatItem}>
              <span className={styles.heroStatValue}>24/7</span>
              <span className={styles.heroStatLabel}>Partner Support</span>
            </div>
          </div>
        </div>

        <div className={styles.heroFooter}>
          <span className={styles.liveDot} />
          <span>B4BARBER Partner Network • All Systems Operational</span>
        </div>
      </div>

      {/* Right Form Section */}
      <div className={styles.formSection}>
        <div className={styles.card}>
          <div className={styles.mobileBrand}>
            <BrandLogo variant="lockup" tone="onDark" />
          </div>

          <div className={styles.cardHeader}>
            <h2 className={styles.formTitle}>Partner Portal</h2>
            <p className={styles.formSubtitle}>Sign in with your registered salon credentials</p>
          </div>

          <form onSubmit={handleSubmit} className={styles.form}>
            {/* Email Field */}
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="vendor-email">
                Registered Email
              </label>
              <div className={styles.inputWrapper}>
                <Mail size={17} className={styles.inputIcon} />
                <input
                  id="vendor-email"
                  type="email"
                  className={styles.inputControl}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="owner@yourbarbershop.com"
                  required
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password Field */}
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel} htmlFor="vendor-password">
                Password
              </label>
              <div className={styles.inputWrapper}>
                <Lock size={17} className={styles.inputIcon} />
                <input
                  id="vendor-password"
                  type={showPassword ? 'text' : 'password'}
                  className={styles.inputControl}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••••••"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className={styles.togglePassBtn}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            <div className={styles.formOptions}>
              <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Encrypted Partner SSL</span>
              <button
                type="button"
                className={styles.link}
                onClick={() => navigate('/forgot-password')}
              >
                Forgot password?
              </button>
            </div>

            <button type="submit" className={styles.submitBtn} disabled={loginMutation.isPending}>
              <span>{loginMutation.isPending ? 'Authenticating…' : 'Sign In to Portal'}</span>
              <ArrowRight size={17} />
            </button>
          </form>

          <div className={styles.formFooterNotice}>
            <Store size={14} style={{ color: '#dc2626' }} />
            <span>Dedicated console for verified B4BARBER salon partners</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
